import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { m as motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Settings, Moon, Sun, Languages, Check, Coins } from "lucide-react";
import { useCurrency, CURRENCIES, type CurrencyCode } from "../../context";
import { localizePath } from "../../lib/localeRoutes";
import { FLAG_ICONS } from "../shared/FlagIcons";

const languages = [
  { code: "en", name: "English" },
  { code: "fr", name: "Français" },
  { code: "nl", name: "Nederlands" },
  { code: "es", name: "Español" },
  { code: "de", name: "Deutsch" },
  { code: "it", name: "Italiano" },
  { code: "pt", name: "Português" },
  { code: "ar", name: "العربية" },
];

interface SettingsMenuProps {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  activeSection: string;
  isNavMenuOpen?: boolean;
  closeNavMenu?: () => void;
}

// Renders language, theme, and currency controls in a sleek multi-column dropdown menu.
export default function SettingsMenu({
  isDarkMode,
  toggleDarkMode,
  activeSection,
  isNavMenuOpen,
  closeNavMenu,
}: SettingsMenuProps) {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { currency, setCurrency } = useCurrency();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Automatically close settings menu when mobile navigation menu opens
  useEffect(() => {
    if (isNavMenuOpen && isOpen) {
      setIsOpen(false);
    }
  }, [isNavMenuOpen, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Block page scrolling when settings menu is open
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Navigates to the same page under the chosen language's URL. LanguageRouter
  // then applies the language, keeping the URL and the rendered content in sync
  // so the page always matches its own canonical tag.
  const changeLanguage = (langCode: string) => {
    setIsOpen(false);
    const target = localizePath(location.pathname, langCode);
    if (target !== location.pathname) {
      navigate({ pathname: target, search: location.search, hash: location.hash });
    } else {
      void i18n.changeLanguage(langCode);
    }
  };

  // Applies selected currency and closes the menu.
  const changeCurrency = (code: CurrencyCode) => {
    setCurrency(code);
    setIsOpen(false);
  };

  // Chooses contrast-safe text color based on current section background.
  const getTextColor = () => {
    if (
      activeSection === "contact" ||
      activeSection === "findus" ||
      activeSection === "nearby" ||
      activeSection === "reviews"
    )
      return "text-white";
    return "text-gray-800 dark:text-white";
  };

  const isRTL =
    i18n.language === "ar" ||
    (typeof document !== "undefined" && document.documentElement.dir === "rtl");

  return (
    <>
      {/* Full-Page Blurred Backdrop rendered at document.body level */}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {isOpen && (
              <motion.div
                key="settings-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                onClick={() => setIsOpen(false)}
                className="fixed inset-0 w-screen h-screen bg-black/40 backdrop-blur-md z-40"
                aria-hidden="true"
              />
            )}
          </AnimatePresence>,
          document.body
        )}

      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => {
            const nextState = !isOpen;
            if (nextState && closeNavMenu) {
              closeNavMenu();
            }
            setIsOpen(nextState);
          }}
          className={`relative z-50 flex items-center justify-center p-2 rounded-lg transition-all duration-300 ${getTextColor()}`}
          aria-label={t("settings.title", "Settings")}
        >
          <Settings
            className={`w-7 h-7 transition-transform duration-300 ${
              isOpen ? "rotate-90" : "hover:rotate-90"
            }`}
          />
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className={`fixed top-[5.25rem] left-4 right-4 mx-auto max-w-[21rem] w-auto md:absolute md:top-full md:mt-9 md:w-80 md:mx-0 ${
                isRTL
                  ? "md:left-0 md:right-auto"
                  : "md:right-0 md:left-auto"
              } py-3 rounded-2xl shadow-2xl border transition-all duration-300 z-50`}
              style={{
                background: isDarkMode
                  ? "linear-gradient(135deg, rgba(26, 15, 10, 0.96), rgba(45, 24, 16, 0.92))"
                  : "linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(245, 240, 230, 0.92))",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                borderColor: isDarkMode
                  ? "rgba(255, 255, 255, 0.12)"
                  : "rgba(151, 110, 56, 0.15)",
                maxHeight: "calc(100vh - 120px)",
                overflowY: "auto",
              }}
            >
          {/* Settings Title */}
          <div
            className={`px-4 pb-2 mb-2 border-b flex items-center justify-between ${
              isDarkMode ? "border-gray-700/60" : "border-sand-200"
            }`}
          >
            <div
              className={`flex items-center gap-2 ${
                isDarkMode ? "text-white" : "text-gray-700"
              }`}
            >
              <Settings className="w-4 h-4 text-caramel-500" />
              <span className="text-xs font-semibold uppercase tracking-wider">
                {t("settings.title")}
              </span>
            </div>
          </div>

          {/* Dark Mode Toggle */}
          <div className="px-4 py-2 mb-1">
            <div
              className={`text-xs font-medium mb-1.5 ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              {t("settings.theme")}
            </div>
            <button
              onClick={toggleDarkMode}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-xl transition-all ${
                isDarkMode
                  ? "bg-gray-800/60 hover:bg-gray-800/80 text-white"
                  : "bg-sand-50/70 hover:bg-sand-100/70 text-gray-800"
              }`}
            >
              <div className="flex items-center gap-2">
                {isDarkMode ? (
                  <Moon className="w-4 h-4 flex-shrink-0 text-amber-300" />
                ) : (
                  <Sun className="w-4 h-4 flex-shrink-0 text-amber-500" />
                )}
                <span className="text-sm font-medium">
                  {isDarkMode
                    ? t("settings.darkMode")
                    : t("settings.lightMode")}
                </span>
              </div>
              <div
                className={`w-10 h-5 rounded-full transition-colors ${
                  isDarkMode ? "bg-caramel-600" : "bg-gray-300"
                } relative flex-shrink-0`}
              >
                <div
                  className={`absolute top-0.5 ${
                    isDarkMode ? "right-0.5" : "left-0.5"
                  } w-4 h-4 rounded-full bg-white transition-all duration-300`}
                ></div>
              </div>
            </button>
          </div>

          {/* Language Selection: 2 per row */}
          <div className="px-4 py-2">
            <div
              className={`flex items-center gap-1.5 text-xs font-medium mb-2 ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              <Languages className="w-3.5 h-3.5 text-caramel-500" />
              <span>{t("settings.language")}</span>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {languages.map((lang) => {
                const FlagIcon = FLAG_ICONS[lang.code];
                return (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`px-2.5 py-2 text-start flex items-center justify-between rounded-xl transition-all ${
                      i18n.language === lang.code
                        ? isDarkMode
                          ? "bg-caramel-600/35 text-white font-semibold ring-1 ring-caramel-500/50"
                          : "bg-caramel-100/70 text-caramel-800 font-semibold ring-1 ring-caramel-400/50"
                        : isDarkMode
                        ? "text-gray-200 hover:bg-white/10"
                        : "text-gray-700 hover:bg-sand-100/60"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 min-w-0">
                      {FlagIcon && <FlagIcon />}
                      <span className="text-xs sm:text-sm truncate">{lang.name}</span>
                    </div>
                    {i18n.language === lang.code && (
                      <Check className="w-3.5 h-3.5 flex-shrink-0 text-caramel-500" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Currency Selection: 3 per row */}
          <div
            className={`px-4 py-2 mt-2 border-t ${
              isDarkMode ? "border-gray-700/60" : "border-sand-200"
            }`}
          >
            <div
              className={`flex items-center gap-1.5 text-xs font-medium mb-2 mt-1 ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              <Coins className="w-3.5 h-3.5 text-caramel-500" />
              <span>{t("settings.currency")}</span>
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {CURRENCIES.map((cur) => (
                <button
                  key={cur.code}
                  onClick={() => changeCurrency(cur.code)}
                  className={`px-2 py-2 text-center rounded-xl transition-all flex flex-col items-center justify-center ${
                    currency.code === cur.code
                      ? isDarkMode
                        ? "bg-caramel-600/35 text-white font-semibold ring-1 ring-caramel-500/50"
                        : "bg-caramel-100/70 text-caramel-800 font-semibold ring-1 ring-caramel-400/50"
                      : isDarkMode
                      ? "text-gray-200 hover:bg-white/10"
                      : "text-gray-700 hover:bg-sand-100/60"
                  }`}
                >
                  <span className="text-sm font-bold text-caramel-600 dark:text-caramel-400">
                    {cur.symbol}
                  </span>
                  <span className="text-[11px] font-medium opacity-90">{cur.code}</span>
                </button>
              ))}
            </div>
          </div>
        </motion.div>
        )}
      </AnimatePresence>
    </div>
    </>
  );
}
